import React, { useState } from 'react';
import { DollarSign, Plus, Minus, Save, X } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const CreditAdjustmentModal = ({ client, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('credit'); // 'credit' ou 'debit'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const adjustmentAmount = mode === 'debit' 
      ? -Math.abs(parseFloat(amount)) 
      : Math.abs(parseFloat(amount));

    try {
      await api.post(`/admin/users/${client.id}/adjust-credit`, {
        amount: adjustmentAmount,
        reason: reason,
        admin_note: adminNote || null
      });
      
      alert('Crédit ajusté avec succès');
      onSuccess();
      onClose();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de l\'ajustement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#1A1A1D] rounded-lg metallic-border max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-[#00FFFF]" />
            <span>Ajuster le solde</span>
          </h3>
          <button onClick={onClose} className="text-[#6B6B6E] hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 p-4 bg-[#222226] rounded-lg">
          <p className="text-[#B8B8BA] text-sm">Client</p>
          <p className="text-white font-semibold">{client.full_name}</p>
          <p className="text-[#00FFFF] text-lg font-bold mt-2">
            Solde actuel: {client.credit_available?.toFixed(2) || '0.00'}€
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mode: Crédit ou Débit */}
          <div>
            <label className="block text-[#E8E8E8] text-sm mb-2">Type d'opération</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode('credit')}
                className={`p-3 rounded-lg border transition-all flex items-center justify-center space-x-2 ${
                  mode === 'credit'
                    ? 'border-[#00FF88] bg-[#00FF88]/10 text-[#00FF88]'
                    : 'border-[#3A3A3D] bg-[#222226] text-[#B8B8BA] hover:border-[#6B6B6E]'
                }`}
              >
                <Plus className="h-4 w-4" />
                <span>Créditer</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('debit')}
                className={`p-3 rounded-lg border transition-all flex items-center justify-center space-x-2 ${
                  mode === 'debit'
                    ? 'border-[#FF3B5C] bg-[#FF3B5C]/10 text-[#FF3B5C]'
                    : 'border-[#3A3A3D] bg-[#222226] text-[#B8B8BA] hover:border-[#6B6B6E]'
                }`}
              >
                <Minus className="h-4 w-4" />
                <span>Débiter</span>
              </button>
            </div>
          </div>

          {/* Montant */}
          <div>
            <label className="block text-[#E8E8E8] text-sm mb-2">Montant (€)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
              placeholder="0.00"
              required
            />
          </div>

          {/* Raison */}
          <div>
            <label className="block text-[#E8E8E8] text-sm mb-2">Raison *</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
              required
            >
              <option value="">Sélectionner...</option>
              <option value="geste_commercial">Geste commercial</option>
              <option value="correction_erreur">Correction d'erreur</option>
              <option value="remboursement">Remboursement</option>
              <option value="bonus_exceptionnel">Bonus exceptionnel</option>
              <option value="compensation_incident">Compensation incident</option>
              <option value="autre">Autre</option>
            </select>
          </div>

          {/* Note admin (optionnelle) */}
          <div>
            <label className="block text-[#E8E8E8] text-sm mb-2">Note interne (optionnel)</label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows="2"
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
              placeholder="Détails supplémentaires..."
            />
          </div>

          {/* Aperçu */}
          {amount && (
            <div className={`p-4 rounded-lg border ${
              mode === 'credit' ? 'bg-[#00FF88]/10 border-[#00FF88]' : 'bg-[#FF3B5C]/10 border-[#FF3B5C]'
            }`}>
              <p className="text-[#B8B8BA] text-sm">Nouveau solde après opération:</p>
              <p className="text-white font-bold text-xl">
                {mode === 'credit'
                  ? ((client.credit_available || 0) + parseFloat(amount)).toFixed(2)
                  : Math.max(0, (client.credit_available || 0) - parseFloat(amount)).toFixed(2)
                }€
              </p>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !amount || !reason}
              className="flex-1 btn-primary py-3 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  <span>Traitement...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  <span>Valider</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreditAdjustmentModal;
